import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/status/status-badge";
import type { StatusTone } from "@/lib/types";

export function formatUnix(ts: number | null | undefined): string {
  if (ts == null || !Number.isFinite(Number(ts))) return "—";
  return new Date(Number(ts) * 1000).toLocaleString();
}

export function formatUnixDate(ts: number | null | undefined): string {
  if (ts == null || !Number.isFinite(Number(ts))) return "—";
  return new Date(Number(ts) * 1000).toLocaleDateString();
}

/** Convert unix seconds → `datetime-local` input value (local). */
export function toDatetimeLocalValue(ts: number | null | undefined): string {
  if (ts == null || !Number.isFinite(Number(ts))) return "";
  const d = new Date(Number(ts) * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Parse `datetime-local` value → unix seconds. */
export function fromDatetimeLocalValue(value: string): number | null {
  if (!value.trim()) return null;
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 1000);
}

export function userStatusTone(status: string): StatusTone {
  switch (status) {
    case "active":
      return "ready";
    case "disabled":
      return "paused";
    case "suspended":
      return "warning";
    default:
      return "neutral";
  }
}

export function billingStatusTone(
  status: string | null | undefined,
): StatusTone {
  switch (status) {
    case "active":
      return "syncing";
    case "trialing":
      return "ready";
    case "past_due":
      return "warning";
    case "canceled":
    case "cancelled":
      return "failed";
    default:
      return "neutral";
  }
}

export function OverriddenBadge({ show }: { show: boolean }) {
  if (!show) return null;
  return <StatusBadge tone="warning">Overridden</StatusBadge>;
}

export function FilterToolbar({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-rs-sm border border-rs-border bg-rs-muted p-3">
      {children}
    </div>
  );
}

export function FilterField({
  id,
  label,
  children,
  className,
}: {
  id?: string;
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={
        className ?? "min-w-0 w-full sm:min-w-[10rem] flex-1 space-y-1"
      }
    >
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

export const selectClassName =
  "flex h-12 w-full rounded-rs-sm border border-rs-border bg-rs-elevated px-4 text-sm text-rs-ink";

export function PaginationBar({
  page,
  limit,
  total,
  onPageChange,
}: {
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(total, page * limit);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-rs-ink-muted">
      <p>
        Showing {from}–{to} of {total}
      </p>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <span>
          Page {page} of {totalPages}
        </span>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

export function SearchInput({
  id,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <Input
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}
